import { useEffect, useState, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import Iconify from "../iconify";
import IconCode from "@/src/lib/iconCode";
import { useDebounce } from "@/src/lib/util/custom-hooks/useDebounce";
import { useTypingPlaceholder } from "@/src/lib/util/custom-hooks/useTypingPlaceholder";
import { headerApiService } from "@/src/lib/api-services/headerApiServices";
import Progressbar from "../loading";
import TestPrice from "./test-search/testPrice";
import CustomButton from "@/src/lib/ui/Button";
import CustomIconButton from "@/src/lib/ui/iconButton";
import useBreakpoint from "@/src/lib/util/custom-hooks/useBreakPoint";
import TextField from "@/src/components/custom-fields/TextField";
import { initiateCall } from "@/src/lib/util/callUtils";

const iconMap = {
    lab: "guidance:mri-pet",
    btc: "hugeicons:blood",
    pathology: "medical-icon:i-pathology",
    radiology: "material-symbols:radiology-outline-rounded",
    package: "hugeicons:package-add",
};

const STATIC_PLACEHOLDER = ["Tests", "Centres", "Checkups"];

// Insert the missing space in "3T MRI" variations (e.g. "3tmri", "3TMRI", "3tmr") before sending to the backend.
const normalizeSearchTerm = (term) => term.replace(/3\s*t\s*mri?/gi, "3T MRI");

function handleApiError(error) {
    if (error?.response) {
        const { status } = error.response;

        if (status === 409) {
            return { status: 409, message: "No results found." };
        } else if (status === 422) {
            return {
                status: 422,
                message: "Search failed! Please retry.",
            };
        } else {
            return {
                status,
                message: "Something went wrong. Please try again.",
            };
        }
    } else {
        return {
            status: "unknown",
            message: "Something went wrong. Please try again.",
        };
    }
}

const HeaderSearch = ({
    textFieldClasses,
    buttonFieldClasses,
    inputSize = "medium",
    layoutShiftToLeft,
}) => {
    const router = useRouter();
    const isMobile = useBreakpoint("sm");
    const containerRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [searchTests, setSearchTests] = useState([]);
    const [errorState, setErrorState] = useState(null);

    const typedText = useTypingPlaceholder({
        placeholders: STATIC_PLACEHOLDER,
        delay: 60,
        pause: 1500,
    });

    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    const fetchData = useCallback(async () => {
        setErrorState(null);

        if (debouncedSearchTerm.length >= 3) {
            setIsLoading(true);
            try {
                const response = await headerApiService.getOpenSearchTest({
                    searchTerm: normalizeSearchTerm(debouncedSearchTerm),
                });
                setSearchTests(response.data);
            } catch (error) {
                const { status, message } = handleApiError(error);
                setErrorState({ status, message });
                setSearchTests([]);
            } finally {
                setIsLoading(false);
            }
        } else {
            setSearchTests([]);
        }
    }, [debouncedSearchTerm]);

    useEffect(() => {
        fetchData();
    }, [debouncedSearchTerm, fetchData]);

    const onSearchTestHandler = (event) => {
        setSearchTerm(event.target.value);
    };

    const clearOverlay = useCallback(() => {
        setSearchTests([]);
        setSearchTerm("");
        setErrorState(null);
    }, []);

    const handleDocumentClick = useCallback(
        (event) => {
            const container = containerRef.current;
            if (container && !container.contains(event.target)) {
                clearOverlay();
            }
        },
        [clearOverlay]
    );

    useEffect(() => {
        document.addEventListener("click", handleDocumentClick);
        return () => {
            document.removeEventListener("click", handleDocumentClick);
        };
    }, [handleDocumentClick]);

    useEffect(() => {
        const onRouteChangeStart = () => clearOverlay();
        router?.events?.on("routeChangeStart", onRouteChangeStart);
        router?.events?.on("hashChangeStart", onRouteChangeStart);
        return () => {
            router?.events?.off("routeChangeStart", onRouteChangeStart);
            router?.events?.off("hashChangeStart", onRouteChangeStart);
        };
    }, [router?.events, clearOverlay]);

    const searchHandler = () => {
        clearOverlay();
        router.push(
            `/find-a-test${
                searchTerm ? `?search=${normalizeSearchTerm(searchTerm)}` : ""
            }`
        );
    };

    return (
        <div ref={containerRef} className={`${textFieldClasses} flex flex-row`}>
            <TextField
                className="mobile_view_placeholder"
                placeholder={`${isMobile ? "Find" : "Search"} ${typedText}`}
                size={inputSize}
                fullWidth
                value={searchTerm}
                onChange={onSearchTestHandler}
            />

            <CustomIconButton
                color="primary"
                //shape="rounded"
                className={`${buttonFieldClasses}`}
                aria-label="Search"
                onClick={searchHandler}
            >
                <Iconify icon={IconCode.search} width="24px" />
            </CustomIconButton>

            {errorState && (
                <div
                    className={`${
                        isMobile
                            ? "fixed left-0 right-0 mt-[100px] mx-3"
                            : "absolute w-full -mt-5"
                    } bg-white shadow-xs rounded-lg md:top-16 top-12 max-h-72 overflow-y-auto overflow-x-hidden z-50 ${
                        !isMobile && layoutShiftToLeft ? "left-0" : ""
                    }`}
                >
                    <div className="flex flex-col">
                        {errorState.status === 409 && (
                            <div className="px-2 py-2 bg-white flex flex-row justify-center">
                                <p className="text-gray-500 text-sm font-bold">
                                    {errorState.message}
                                </p>
                            </div>
                        )}

                        {errorState.status === 422 && (
                            <div className="px-2 py-2 bg-white hover:bg-gray-100 cursor-pointer flex flex-row justify-between items-center gap-2">
                                <p className="text-gray-500 text-sm font-bold">
                                    {errorState.message}
                                </p>

                                <CustomButton onClick={fetchData}>
                                    <p className="text-primary text-sm font-bold">
                                        Retry
                                    </p>
                                </CustomButton>
                            </div>
                        )}

                        {errorState.status !== 409 &&
                            errorState.status !== 422 && (
                                <div className="px-2 py-2 bg-white flex flex-row justify-center">
                                    <p className="text-gray-500 text-sm font-bold">
                                        {errorState.message}
                                    </p>
                                </div>
                            )}
                    </div>
                </div>
            )}

            {!errorState && searchTests.length > 0 && (
                <div
                    className={`${
                        isMobile
                            ? "fixed left-0 right-0 mt-[100px] mx-3"
                            : "absolute w-full -mt-5"
                    } bg-white shadow-xs rounded-lg md:top-16 top-12 max-h-72 overflow-y-auto overflow-x-hidden z-50 ${
                        !isMobile && layoutShiftToLeft ? "left-0" : ""
                    }`}
                >
                    <div className="flex flex-col">
                        {searchTests.map((item, index) => (
                            <div
                                key={index}
                                className="px-2 py-2 border-b border-gray-300 hover:bg-gray-100 cursor-pointer flex flex-row justify-between items-center"
                                onClick={() => {
                                    setSearchTests([]);
                                    setSearchTerm("");
                                    //window.location.href = `/${item?.redirect_slug}`;
                                    router.push(`/${item?.redirect_slug}`);
                                }}
                            >
                                <div className="flex flex-row items-center gap-1">
                                    <Iconify
                                        icon={iconMap[item?.icon_type]}
                                        className="text-black w-6 h-6 shrink-0"
                                    />
                                    <p className="text-black text-sm font-bold">
                                        {item?.search_name}
                                    </p>
                                </div>

                                {item?.is_centre === 1 ? (
                                    <div className="flex flex-row items-center gap-1">
                                        <p className="text-black text-sm font-bold">
                                            {item?.price_details?.phone}
                                        </p>
                                        <Iconify
                                            icon="line-md:phone-call-loop"
                                            className="text-primary"
                                            width="18px"
                                            onClick={() =>
                                                initiateCall(
                                                    item?.price_details?.phone
                                                )
                                            }
                                        />
                                    </div>
                                ) : (
                                    <TestPrice
                                        price={item?.price_details?.configPrice}
                                        minPrice={item?.price_details?.minPrice}
                                        maxPrice={item?.price_details?.maxPrice}
                                        textColor="text-black"
                                    />
                                )}
                            </div>
                        ))}

                        <div
                            className="px-2 py-2 bg-white hover:bg-gray-100 cursor-pointer flex flex-row justify-center sticky bottom-0"
                            onClick={() => {
                                setSearchTests([]);
                                router.push(
                                    `/find-a-test?search=${normalizeSearchTerm(
                                        searchTerm
                                    )}`
                                );
                                setSearchTerm("");
                            }}
                        >
                            <p className="text-primary text-sm font-bold">
                                Show All Results
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {!errorState &&
                debouncedSearchTerm.length >= 3 &&
                !isLoading &&
                searchTests.length === 0 && (
                    <div
                        className={`${
                            isMobile
                                ? "fixed left-0 right-0 mt-[100px] mx-3"
                                : "absolute w-full -mt-5"
                        } bg-white shadow-xs rounded-lg md:top-16 top-12 max-h-72 overflow-y-auto overflow-x-hidden z-50 ${
                            !isMobile && layoutShiftToLeft ? "left-0" : ""
                        }`}
                    >
                        <div className="flex flex-col">
                            <div className="px-2 py-2 bg-white flex flex-row justify-center">
                                <p className="text-gray-500 text-sm font-bold">
                                    No Results Found
                                </p>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

export default HeaderSearch;
